import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes, AppNavPaths } from '../Common';
import { ExecutionHistory } from './common/ExecutionHistory';
import AdminToolIndex from './AdminToolIndex';
import { SupportToolLayout } from './SupportToolLayout';
import { getSupportToolRegistryItem } from './supportTools.registry';

export const SupportToolPage = ({
    tool,
    usageHistory,
    refreshUsageHistory,
}) => {
    const { FormComponent, HistoryDetailsComponent } = tool;
    const location = useLocation();
    const isAdminToolPath = location?.pathname?.startsWith(AppNavPaths.AdminTool);

    return (
        <SupportToolLayout
            title={tool.name}
            description={tool.description}
            examples={tool.examples}
            warnings={tool.warnings}
            tips={tool.tips}
        >
            <div className="bg-light p-4 rounded border mb-4">
                <FormComponent
                    onSuccess={refreshUsageHistory}
                    isAdminToolPath={isAdminToolPath}
                />
            </div>

            {tool.adminToolId && (
                <ExecutionHistory
                    usageHistory={usageHistory}
                    renderDetails={(record) => (
                        <HistoryDetailsComponent record={record} />
                    )}
                />
            )}
        </SupportToolLayout>
    );
};

export const SupportTools = () => {
    const { toolId } = useParams();
    const [usageHistory, setUsageHistory] = useState([]);

    const refreshUsageHistory = useCallback(async () => {
        if (!toolId) return;

        const adminToolId = getSupportToolRegistryItem(toolId)?.adminToolId;
        if (!adminToolId) return;

        try {
            const history = await httpClient.js(
                ApiRoutes.adminTool.getAdminToolHistory(adminToolId),
            );
            setUsageHistory(history);
        } catch (err) {
            console.error('Failed to fetch usage history:', err);
        }
    }, [toolId]);

    useEffect(() => {
        if (toolId) {
            void refreshUsageHistory();
        }
    }, [toolId, refreshUsageHistory]);

    if (!toolId) {
        return <AdminToolIndex supportOnly />;
    }

    const tool = getSupportToolRegistryItem(toolId);

    if (!tool) {
        return (
            <div className="p-5 bg-light w-100">
                <h2>Tool Not Found</h2>
                <p>The requested support tool does not exist.</p>
            </div>
        );
    }

    return (
        <SupportToolPage
            tool={tool}
            usageHistory={usageHistory}
            refreshUsageHistory={refreshUsageHistory}
        />
    );
};
